import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { AssistantCandidate } from '../../../../types/assistant.types';
import { LocationState } from '../../../../types/locationState.interface';
import { RoutesConstant } from '../../../../constants/routes.constant';
import {
  getAssistantMessageIdFromElement,
  sortAssistantCandidates,
} from '../../../../utils/assistant.utils';
import {
  StyledCandidateResultsList,
  StyledOpenAssistantLink,
} from '../Assistant.styles';
import CandidateResultCard, {
  CandidateResultCardVariant,
} from './CandidateResultCard.component';

interface CandidateResultsListProps {
  candidates: AssistantCandidate[];
  variant?: CandidateResultCardVariant;
  showOpenAssistant?: boolean;
  limit?: number;
}

const CandidateResultsList = ({
  candidates,
  variant = 'full',
  showOpenAssistant = false,
  limit,
}: CandidateResultsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthHook();

  if (!candidates.length) {
    return null;
  }

  const sortedCandidates = sortAssistantCandidates(candidates);
  const visibleCandidates =
    limit && limit > 0 ? sortedCandidates.slice(0, limit) : sortedCandidates;
  const remainingCount = sortedCandidates.length - visibleCandidates.length;

  const canOpenCandidate = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_CANDIDATE,
  ]);

  const handleOpenAssistant = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const focusMessageId = getAssistantMessageIdFromElement(event.currentTarget);
    const state: LocationState | undefined = focusMessageId
      ? { focusMessageId }
      : undefined;

    navigate(RoutesConstant.ASSISTANT, { state });
  };

  return (
    <StyledCandidateResultsList>
      {visibleCandidates.map(candidate => (
        <CandidateResultCard
          key={`${candidate.person_id}-${candidate.source}`}
          candidate={candidate}
          canOpenCandidate={canOpenCandidate}
          variant={variant}
        />
      ))}
      {showOpenAssistant && remainingCount > 0 && (
        <StyledOpenAssistantLink type="button" onClick={handleOpenAssistant}>
          {t('assistant.miniChatMore', { count: remainingCount })}
        </StyledOpenAssistantLink>
      )}
    </StyledCandidateResultsList>
  );
};

export default CandidateResultsList;
